import React, { useState, useEffect, useReducer, useContext } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import qs from 'qs'
import isEmpty from 'lodash/isEmpty'
import { Col, notification, Row, Typography, Button } from 'antd'
import * as Sentry from '@sentry/react'
import { ClientContext, useManualQuery } from 'graphql-hooks'

import { getCompanyAndUserInfo } from '../../../graphql/custom-queries'
import { SeoTags } from '../../../components/seo-tags'
import ConnectPlatform from '../components/connect-platform'
import LeaveTrackingInfo from '../components/leave-tracking-info'
import CompletePlanTrial from '../components/complete-plan-trial'
import CreateCompany from '../components/create-company'
import PersonalInfo from '../components/personal-info'
import Testimonials from '../components/testimonials'

import { initialState, reducer } from './reducer'
import { actions } from './actions'
import { useAppDispatch } from '../../../store/hooks'
import { setAuthState } from '../../../store/auth-state-slice'
import { setLocale } from '../../../store/locale-slice'
import { setAuthCompany } from '../../../store/auth-company-slice'
import { setUserId } from '../../../store/user-id-slice'
import { useFeatureFlagEnabled } from 'posthog-js/react'

import { signup } from '../../../services/api/companies'
import { SlackAuth } from '../../../services/auth/slack/slack'
import { MicrosoftAuth } from '../../../services/auth/microsoft/microsoft'
import { GoogleAuth } from '../../../services/auth/google/google'
import { track, identifyCompany, identify } from '../../../services/analytics/analytics'
import { logout } from '../../../services/auth/logout-handler'
import { ISignupRequest } from '../../../services/api/companies.types'
import { parseAndPrepareUtmCookie } from '../../../util/get-and-parse-cookie'
import { getCookieByName } from '../../../util/get-and-parse-cookie'
import { openSupportChat } from '../../../util/open-support-chat'
import { hideAvailabilityTooltip } from '../../../util/set-crisp-session-info'
import { rememberProviderAccount, saveRememberedLogin } from '../../../util/remembered-login'

import { availableLanguages } from '@vacationtracker/shared/i18n'
import { countries, subdivisionOptions } from '@vacationtracker/shared/data/countries'
import { getTimeZoneOrDefaultToUtc } from '@vacationtracker/shared/functions/timezone'
import { wait } from '@vacationtracker/shared/functions/wait'
import { vtUserId } from '@vacationtracker/shared/functions/user-id'
import { UserExistsException } from '@vacationtracker/shared/errors/users'
import { signin, IAuthNextStep, updatePassword, getAuthTokens } from '@vacationtracker/shared/services/auth'
import { confirmSigninWithCustomChallengeAnswer } from '@vacationtracker/shared/services/auth'

import { trackConversionEvent } from '../../../services/analytics/analytics'
import { SIGNUP_SUCCESS_EVENT, sharedStepAuthFlow } from '../../../services/analytics/auth-events'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { setAuthUser, setIsSigningUp } from '../../../store/auth-user-slice'
import { IConnect } from '../types'
import { FrontendUrls } from '../../../types/urls'
import { ICreateUser } from './types'
import { ICreateCompany } from './types'
import { getSavedSubscriptionPeriod, SUBSCRIPTION_PERIOD_STORAGE_KEY } from '../subscription-period'
import { IGetCompanyAndUserInfo } from '../../../types/custom-queries'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import IntlMessages from '../../../util/IntlMessages'

const { Paragraph, Text } = Typography

if (!process.env.REACT_APP_MICROSOFT_CLIENT_ID || !process.env.REACT_APP_SLACK_CLIENT_ID || !process.env.REACT_APP_GOOGLE_CLIENT_ID) {
  throw new Error('Client ID are required')
}

const msAuth = new MicrosoftAuth(process.env.REACT_APP_MICROSOFT_CLIENT_ID)
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID)
const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID)

const CreateCompanyPage = ({ location }: IConnect): React.ReactElement => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const gqlClient = useContext(ClientContext)
  const [state, dispatch] = useReducer(reducer, initialState)
  const reduxDispatch = useAppDispatch()
  const onboardingV2Enabled = useFeatureFlagEnabled(FeatureFlagEnum.onboardingV2)

  const [getCompanyAndUserInfoQuery] = useManualQuery<IGetCompanyAndUserInfo, { userId: string }>(getCompanyAndUserInfo)

  const [focusedPlatform, setFocusedPlatform] = useState<PlatformEnum | null>(null)

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })

  useEffect(() => {
    const path = history.location.pathname
    const createUser = JSON.parse(localStorage.getItem('vtCreateUser') || '{}')

    if (createUser && createUser.name !== '') {
      dispatch(actions.setCreateUser(createUser))
    }

    if ((path === `${FrontendUrls.signup}${FrontendUrls.companyDetails}` || path === `${FrontendUrls.signup}${FrontendUrls.selectPlan}`) && (!createUser || !createUser.platform)) {
      history.push(FrontendUrls.signup)
      dispatch(actions.setCurrentStep('connectPlatform'))
    }

    // setCrispSessionInfoForSignupProcess(createUser.platform)

    const createCompany = JSON.parse(localStorage.getItem('vtCreateCompany') || '{}')

    const selectedPlanFromStorage = localStorage.getItem('vtSubscriptionPlan') as SubscriptionPlanEnum | null
    let selectedPlan: SubscriptionPlanEnum | undefined
    if (selectedPlanFromStorage && [SubscriptionPlanEnum.core, SubscriptionPlanEnum.complete].includes(selectedPlanFromStorage)) {
      selectedPlan = selectedPlanFromStorage
      dispatch(actions.setPlan(selectedPlan))
    }

    prepareCompanyForStateAndDispatchSetter(createUser, createCompany, selectedPlan)
  }, [])

  useEffect(() => {
    hideAvailabilityTooltip()
  }, [])

  const prepareCompanyForStateAndDispatchSetter = (data: ICreateUser, companyFromStorage: ICreateCompany, selectedPlan?: SubscriptionPlanEnum) => {
    const newCompany: ICreateCompany = {
      contactEmail: data.mail,
      name: '',
      platform: data.platform,
      country: '',
      ...(!companyFromStorage.plan && { plan: selectedPlan || SubscriptionPlanEnum.complete }),
      ...(companyFromStorage.plan && { plan: companyFromStorage.plan }),
    }

    if (data.platform === PlatformEnum.Microsoft && data.mail.includes('onmicrosoft.com')) {
      newCompany.contactEmail = ''
    }

    if (data.platform === PlatformEnum.Slack) {
      newCompany.name = data.teamName || ''
    }

    dispatch(actions.setCreateCompanyDetails(isEmpty(companyFromStorage) ? newCompany : companyFromStorage))
  }

  useEffect(() => {
    if (queryParams?.platform) {
      setFocusedPlatform(queryParams.platform as PlatformEnum)
    }
  }, [])

  useEffect(() => {
    const path = history.location.pathname

    if (path === FrontendUrls.signup) {
      dispatch(actions.setCurrentStep('connectPlatform'))
    }

    if (path === `${FrontendUrls.signup}${FrontendUrls.companyDetails}`) {
      dispatch(actions.setCurrentStep('createCompany'))
    }

    if (path === `${FrontendUrls.signup}${FrontendUrls.personalInfo}`) {
      dispatch(actions.setCurrentStep('personalInfo'))
    }
  }, [history.location.pathname])

  const goToLogin = (state?: string) => {
    reduxDispatch(setAuthState(AuthStateEnum.signIn))
    const url = FrontendUrls.signin + (state ? `?${state}=true` : '')
    history.push(url)
  }

  let numberOfRetry = 0
  const getCompanyAndUser = async (id: string) => {
    try {
      const response = await getCompanyAndUserInfoQuery({ variables: { userId: id } })
      if (!response.data || response.error) throw response.error
      if (response.data.getCompany && response.data.getUser && response.data.getUser.name) {
        reduxDispatch(setAuthCompany(response.data.getCompany))
        reduxDispatch(setAuthUser(response.data.getUser))
        if (response.data.getUser.locale) {
          reduxDispatch(setLocale(availableLanguages[response.data.getUser.locale]))
        }
      } else {
        throw new Error('No current user, retry')
      }
    } catch (error) {
      if (numberOfRetry >= 10) {
        // Log only before logout
        // logger.warning('ERROR GET COMPANY AND USER', error, numberOfRetry, location.pathname)
        logout({
          history,
          reduxDispatch,
          userId: id,
        })
      } else if (![FrontendUrls.signin, FrontendUrls.signup].includes(location.pathname as FrontendUrls)) {
        numberOfRetry++
        await wait(200 * numberOfRetry)
        return await getCompanyAndUser(id)
      }
    }
  }

  const authSigin = async (username: string, token: string, platform: string, plan: string) => {
    const signInResponse = (await signin(vtUserId(username, platform as PlatformEnum))) as IAuthNextStep
    if (signInResponse.challengeName === 'CUSTOM_CHALLENGE' && signInResponse.challengeParam?.question === 'token') {
      const cognitoResponse = await confirmSigninWithCustomChallengeAnswer(token, { loginType: platform })
      localStorage.setItem('userId', cognitoResponse.username)
      try {
        const authToken = await getAuthTokens()
        if (gqlClient && authToken.idToken) {
          gqlClient.setHeader('Authorization', authToken.idToken)
        }
      } catch (error) {
        // Swallow the error
      }
      reduxDispatch(setUserId(cognitoResponse.username))
      saveRememberedLogin({ email: state.createUser.mail || state.createCompany.contactEmail, platform: platform as PlatformEnum })
      rememberProviderAccount({ email: state.createUser.mail || state.createCompany.contactEmail, platform })

      await getCompanyAndUser(cognitoResponse.username)

      if (cognitoResponse.attributes['custom:companyId'] && typeof cognitoResponse.attributes['custom:companyId'] === 'string') {
        identify(cognitoResponse.attributes['custom:companyId'], {
          // totalUsers: allUsers.length,  // We don't take total number of users from a directory for this flow
          activeUsers: 1,
        })
        await identifyCompany(cognitoResponse.attributes['custom:companyId'], {
          includeInAiOnboardingAbTest: true, // Include all new users to AI onboarding AB test
          // totalUsers: allUsers.length, // We don't take total number of users from a directory for this flow
          activeUsers: 1,
        })
        await wait(300) // Wait for Posthog to process the identify event
      }

      track('SIGNUP_COMPLETED', {
        platform,
        signupVariation: 'B_2025_06',
        plan,
        timestamp: new Date().toISOString(),
        activeUsers: 1,
        source: 'app',
        status: 'completed',
      })
      trackSignupSuccess(platform as PlatformEnum)
      trackConversionEvent('SIGNUP_COMPLETED')

      clearSignupStorage()
      reduxDispatch(setAuthState(AuthStateEnum.signedIn))
      history.push(`${FrontendUrls.home}?onboardingV2=true`)
    }
  }

  /** Closes the funnel the sign-up page opened, by event name rather than by a property. */
  const trackSignupSuccess = (platform: PlatformEnum) => {
    const successEvent = SIGNUP_SUCCESS_EVENT[platform]
    const authFlow = sharedStepAuthFlow()
    if (successEvent && authFlow.authFlow) {
      track(successEvent, { platform, ...authFlow })
    }
  }

  const clearSignupStorage = () => {
    localStorage.removeItem('vtCreateUser')
    localStorage.removeItem('vtCreateCompany')
    localStorage.removeItem('vtSubscriptionPlan')
    localStorage.removeItem(SUBSCRIPTION_PERIOD_STORAGE_KEY)
    // The address belongs to the sign-up that just finished. Left behind, it prefills the next
    // one with an address that now has an account.
    sessionStorage.removeItem('vtUserEmail')
  }

  const onFinishSetup = async () => {
    dispatch(actions.isCreateCompanyLoader(true))
    const createUser = state.createUser
    let createCompany = state.createCompany
    if (createCompany.name === '' || createCompany.country === '' || !createCompany.plan) {
      const createCompanyFromStorage = JSON.parse(localStorage.getItem('vtCreateCompany') || '{}')
      createCompany = createCompanyFromStorage
    }
    const platform = createUser.platform as PlatformEnum
    let currentPlan = createCompany.plan || SubscriptionPlanEnum.complete

    const selectedPlanFromStorage = localStorage.getItem('vtSubscriptionPlan') as SubscriptionPlanEnum | null
    if (selectedPlanFromStorage && [SubscriptionPlanEnum.core, SubscriptionPlanEnum.complete].includes(selectedPlanFromStorage) && !createCompany.plan) {
      currentPlan = selectedPlanFromStorage
    }

    // Freemium plan is no longer offered, coerce it to Complete
    if (currentPlan === SubscriptionPlanEnum.freemium) {
      currentPlan = SubscriptionPlanEnum.complete
    }
    // Only sent when the signup link set it; the backend defaults the trial to monthly.
    const subscriptionPeriod = getSavedSubscriptionPeriod()

    try {
      const data: ISignupRequest = {
        signupVariation: state.flow,
        user: {
          id: createUser.id,
          name: createUser.name,
          firstName: createUser.firstName,
          lastName: createUser.lastName,
          email: createUser.mail || createCompany.contactEmail,
          timezone: createUser.timezone,
          imageUrl: createUser.imageUrl,
          platform: platform,
          isAdmin: true,
          // TODO: add locale
          locale: LocaleEnum.en,
        },
        company: {
          name: createCompany.name,
          contactEmail: createUser.mail || createCompany.contactEmail,
          platform: platform,
          country: countries.find((country) => country.iso === createCompany.country)?.name as string,
          state: createCompany.state ? subdivisionOptions(createCompany.country).find((subdivision) => subdivision.value === createCompany.state)?.label : undefined,
          daysPerYear: 20,
          hasUnlimitedDays: false,
          timezone: getTimeZoneOrDefaultToUtc(),
          plan: currentPlan,
          ...(subscriptionPeriod && { period: subscriptionPeriod }),
          paymentProcessor: 'stripe',
          affiliateCode: localStorage.getItem('vtAffiliateCode') || undefined,
          surveyQuestionMainOption: createCompany.surveyQuestionMainOption,
          ...(createCompany.surveyQuestionSubOption ? { surveyQuestionSubOption: createCompany.surveyQuestionSubOption } : {}),
        },
      }

      if (platform === PlatformEnum.Microsoft) {
        data.token = msAuth.getTokens().accessToken
        data.ms = {
          msTeamId: createUser.tenantId,
          organizationId: createUser.tenantId,
        }
      }

      if (platform === PlatformEnum.Slack) {
        data.slack = {
          slackBotToken: createUser.slackBotToken,
          slackUserToken: createUser.slackUserToken,
          slackUserId: createUser.slackUserId,
          slackTeamId: createUser.slackTeamId,
          slackOrgTeamId: createUser.slackOrgTeamId ? createUser.slackOrgTeamId : createUser.slackTeamId,
          isOwner: createUser.isOwner || false,
          slackBotId: createUser.slackBotId,
        }
        data.token = slackAuth.getUserToken()
      }

      if (platform === PlatformEnum.Google) {
        data.token = googleAuth.getAccessToken()
        data.google = {
          googleDomain: createUser.googleDomain,
        }
      }

      const utmCookie = getCookieByName('_vt_sc')
      if (utmCookie) {
        try {
          const utmCookieParsed = parseAndPrepareUtmCookie(utmCookie)
          data.utmCookie = utmCookieParsed
        } catch (error) {
          Sentry.captureException(error)
        }
      }

      const response = await signup(data)
      await wait(1000)
      if (data.token) {
        // Log in with token
        await authSigin(createUser.id, data.token, platform, currentPlan)
      } else if (platform === 'email') {
        reduxDispatch(setIsSigningUp(true))
        const userId = response.userId
        let signedInUser
        if (response.temporaryPassword) {
          // Log in with password
          const temporaryPassword: string = response.temporaryPassword
          const cognitoResponse = (await signin(userId, temporaryPassword)) as IAuthNextStep

          if (cognitoResponse.challengeName === 'NEW_PASSWORD_REQUIRED') {
            await confirmSigninWithCustomChallengeAnswer(createUser.userPassword)
          } else {
            await updatePassword(temporaryPassword, createUser.userPassword)
          }
          signedInUser = await signin(userId, createUser.userPassword)
        }

        localStorage.setItem('userId', userId)
        reduxDispatch(setUserId(userId))
        // The username is what lets the next visit open on the password step rather than
        // asking for the address again.
        saveRememberedLogin({
          email: createUser.mail || createCompany.contactEmail,
          platform: PlatformEnum.Email,
          username: userId,
        })
        await getCompanyAndUser(userId)

        if (signedInUser?.companyId) {
          identifyCompany(signedInUser.companyId, {
            includeInAiOnboardingAbTest: true, // Include all new users to AI onboarding AB test
            activeUsers: 1,
          })
        }

        track('SIGNUP_COMPLETED', {
          platform,
          signupVariation: 'B_2025_06',
          plan: state.createCompany.plan,
          timestamp: new Date().toISOString(),
          source: 'app',
          status: 'completed',
        })
        trackSignupSuccess(platform)
        trackConversionEvent('SIGNUP_COMPLETED')

        clearSignupStorage()
        reduxDispatch(setAuthState(AuthStateEnum.signedIn))
        history.push(FrontendUrls.setup)
      }
    } catch (error) {
      dispatch(actions.isCreateCompanyLoader(false))
      if (error.response?.data?.code === UserExistsException.code) {
        const { data } = error.response.data
        track('SIGNUP_ERROR', {
          platform,
          signupVariation: 'B_2025_06',
          errorCode: UserExistsException.code,
          errorMessage: UserExistsException.message,
          plan: state.createCompany.plan,
        })
        notification.error({
          message: formatMessage({ id: 'error.signup.alreadySignedUp.title' }),
          description:
            data.status.toUpperCase() === 'ACTIVE' ? (
              <>
                <Paragraph>
                  <IntlMessages
                    id="error.signup.alreadySignedUp.descriptionLine1"
                    values={{
                      email: data.email,
                      text: (chunks) => <Text keyboard>{...chunks}</Text>,
                    }}
                  />
                </Paragraph>
                <Paragraph>{formatMessage({ id: 'error.signup.alreadySignedUp.descriptionLine2' })}</Paragraph>
              </>
            ) : (
              <>
                <Paragraph>
                  <IntlMessages
                    id="error.signup.alreadySignedUpInactive.descriptionLine1"
                    values={{
                      email: data.email,
                      text: (chunks) => <Text keyboard>{...chunks}</Text>,
                    }}
                  />
                </Paragraph>
                <Paragraph>
                  <IntlMessages id="error.signup.alreadySignedUpInactive.descriptionLine2" />
                </Paragraph>
                <Paragraph code copyable>
                  {JSON.stringify({ cid: data.companyId, uid: data.id }, null, 4)}
                </Paragraph>
              </>
            ),
          key: UserExistsException.code,
          btn:
            data.status.toUpperCase() === 'ACTIVE' ? (
              <Button
                onClick={() => {
                  notification.destroy(UserExistsException.code)
                  goToLogin()
                }}
              >
                {formatMessage({ id: 'app.login' })}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  notification.destroy(UserExistsException.code)
                  openSupportChat()
                }}
              >
                {formatMessage({ id: 'app.contactSupport' })}
              </Button>
            ),
        })
        return
      }
      const errorDescription = error.response?.data ? error.response?.data : error.message ? error.message : JSON.stringify(error)
      track('SIGNUP_ERROR', {
        platform,
        errorMessage: errorDescription,
        plan: state.createCompany.plan,
      })
      notification.error({
        message: formatMessage({ id: 'error.somethingWentWrong' }),
        description: errorDescription,
      })
      if (error.response?.data.includes('ValidationError')) {
        history.push('/signup/company-details')
        dispatch(actions.setCurrentStep('createCompany'))
      } else {
        clearSignupStorage()
        reduxDispatch(setAuthState(AuthStateEnum.signIn))
      }
      reduxDispatch(setIsSigningUp(false))
    }
  }

  const steps = {
    connectPlatform: {
      id: 'connectPlatform',
      content: (
        <ConnectPlatform
          focusedPlatform={focusedPlatform}
          goToLogin={goToLogin}
          dispatch={dispatch}
          reduxDispatch={reduxDispatch}
          msAuth={msAuth}
          slackAuth={slackAuth}
          googleAuth={googleAuth}
        />
      ),
      sidebarInfo: <LeaveTrackingInfo selectedPlan={state.createCompany.plan} />,
    },
    createCompany: {
      id: 'createCompany',
      content: (
        <CreateCompany
          dispatch={dispatch}
          state={state}
          onFinishSetup={() => {
            onFinishSetup()
          }}
        />
      ),
      sidebarInfo: <CompletePlanTrial />,
    },
    personalInfo: {
      id: 'personalInfo',
      content: <PersonalInfo dispatch={dispatch} state={state} />,
      sidebarInfo: <Testimonials />,
    },
  }

  return (
    <div className="auth-wrap">
      <SeoTags title="connect.meta.title.signup" description="connect.meta.description" />
      <div style={{ height: '100vh' }} id={steps[state.currentStep].id}>
        <Row style={{ height: '100%' }}>
          <Col span={12} className="auth-sidebar-info">
            {steps[state.currentStep].sidebarInfo}
          </Col>
          <Col span={12} className="auth-sidebar-form">
            {steps[state.currentStep].content}
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default CreateCompanyPage
